import { useCallback, useEffect, useRef, useState, type PointerEvent } from "react";

type Fragment = { x: number; y: number; taken: boolean };
type ErrorBlock = { x: number; y: number; w: number; h: number; vx: number; vy: number };
type GameState = "idle" | "playing" | "won" | "lost";

const messages = ["NULL REFERENCE", "BUILD FAILED", "FIX DEPLOYED", "MEMORY RESTORED", "BUILD SUCCESSFUL"];
const keys = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "w", "a", "s", "d", "W", "A", "S", "D"]);

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function hitCircleRect(cx: number, cy: number, r: number, rect: ErrorBlock) {
  const x = clamp(cx, rect.x, rect.x + rect.w);
  const y = clamp(cy, rect.y, rect.y + rect.h);
  return (cx - x) ** 2 + (cy - y) ** 2 <= r ** 2;
}

export default function TerminalMiniGame() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number | undefined>(undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  const keysRef = useRef(new Set<string>());
  const pointerRef = useRef<{ active: boolean; x: number; y: number }>({ active: false, x: 0, y: 0 });
  const visibleRef = useRef(true);
  const documentVisibleRef = useRef(!document.hidden);
  const reduceMotionRef = useRef(false);
  const hoverRef = useRef(false);
  const focusRef = useRef(false);
  const playableRef = useRef(true);
  const gameRef = useRef({
    state: "idle" as GameState,
    w: 260,
    h: 120,
    start: 0,
    last: 0,
    health: 3,
    hurtUntil: 0,
    player: { x: 50, y: 50, r: 5 },
    fragments: [] as Fragment[],
    errors: [] as ErrorBlock[],
    exit: { x: 0, y: 0, r: 9 },
    terminalTick: 0
  });
  const [state, setState] = useState<GameState>("idle");
  const [playable, setPlayable] = useState(true);

  const drawIdle = useCallback((ctx: CanvasRenderingContext2D, now: number, activeHint = false) => {
    const game = gameRef.current;
    const { w, h } = game;
    ctx.fillStyle = "#020704";
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "rgba(42,255,106,0.08)";
    for (let y = 0; y < h; y += 4) ctx.fillRect(0, y, w, 1);
    ctx.fillStyle = "rgba(31,255,92,0.75)";
    ctx.font = `${Math.max(8, Math.floor(w / 30))}px ui-monospace, SFMono-Regular, Consolas, monospace`;
    const lines = [
      "> SZ_DEBUG_PORT",
      "> MEMORY MAP: ONLINE",
      `> ${messages[Math.floor((now / 1200) % messages.length)]}`,
      "> AWAITING INPUT_"
    ];
    lines.forEach((line, index) => {
      const flicker = reduceMotionRef.current ? 0 : Math.sin(now / 240 + index) * 2;
      ctx.fillText(line, 10, 18 + index * 18 + flicker);
    });
    if ((hoverRef.current || focusRef.current || activeHint) && playableRef.current && !reduceMotionRef.current) {
      ctx.fillStyle = "rgba(2,7,4,0.68)";
      ctx.fillRect(0, h - 28, w, 28);
      ctx.fillStyle = "#c83c36";
      ctx.fillText("CLICK TO DEBUG", 10, h - 10);
    }
  }, []);

  const resetGame = useCallback(() => {
    const game = gameRef.current;
    const { w, h } = game;
    game.state = "playing";
    game.start = performance.now();
    game.last = game.start;
    game.health = 3;
    game.hurtUntil = 0;
    game.player = { x: w * 0.16, y: h * 0.5, r: Math.max(4, Math.min(w, h) * 0.035) };
    game.fragments = [
      { x: w * 0.42, y: h * 0.25, taken: false },
      { x: w * 0.66, y: h * 0.7, taken: false },
      { x: w * 0.84, y: h * 0.34, taken: false }
    ];
    game.errors = Array.from({ length: w > 250 ? 5 : 3 }, (_, index) => ({
      x: w * (0.26 + index * 0.12),
      y: h * (0.18 + (index % 3) * 0.22),
      w: Math.max(12, w * 0.075),
      h: Math.max(8, h * 0.09),
      vx: (index % 2 ? -1 : 1) * (10 + index * 2),
      vy: (index % 2 ? 1 : -1) * (7 + index)
    }));
    game.exit = { x: w - 18, y: h / 2, r: Math.max(7, Math.min(w, h) * 0.05) };
    setState("playing");
  }, []);

  const activate = useCallback(() => {
    if (!playableRef.current || reduceMotionRef.current) return;
    resetGame();
  }, [resetGame]);

  const drawPlaying = useCallback((ctx: CanvasRenderingContext2D, now: number, dt: number) => {
    const game = gameRef.current;
    const { w, h, player } = game;
    const elapsed = (now - game.start) / 1000;
    const remaining = Math.max(0, 25 - elapsed);

    ctx.fillStyle = "#020704";
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = "rgba(42,255,106,0.07)";
    for (let y = 0; y < h; y += 4) ctx.fillRect(0, y, w, 1);
    ctx.strokeStyle = "rgba(53,255,112,0.12)";
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += 22) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }

    const speed = Math.min(w, h) * 0.72;
    let dx = 0;
    let dy = 0;
    const pressed = keysRef.current;
    if (pressed.has("ArrowLeft") || pressed.has("a") || pressed.has("A")) dx -= 1;
    if (pressed.has("ArrowRight") || pressed.has("d") || pressed.has("D")) dx += 1;
    if (pressed.has("ArrowUp") || pressed.has("w") || pressed.has("W")) dy -= 1;
    if (pressed.has("ArrowDown") || pressed.has("s") || pressed.has("S")) dy += 1;
    if (pointerRef.current.active) {
      dx += clamp(pointerRef.current.x - player.x, -1, 1);
      dy += clamp(pointerRef.current.y - player.y, -1, 1);
    }
    const len = Math.hypot(dx, dy) || 1;
    player.x = clamp(player.x + (dx / len) * speed * dt, player.r, w - player.r);
    player.y = clamp(player.y + (dy / len) * speed * dt, player.r, h - player.r);

    game.errors.forEach((err, index) => {
      err.x += err.vx * dt;
      err.y += err.vy * dt;
      if (err.x < 0 || err.x + err.w > w) err.vx *= -1;
      if (err.y < 20 || err.y + err.h > h) err.vy *= -1;
      ctx.fillStyle = index % 2 ? "rgba(165,35,32,0.68)" : "rgba(110,18,18,0.72)";
      ctx.fillRect(err.x, err.y, err.w, err.h);
      ctx.strokeStyle = "rgba(255,94,86,0.8)";
      ctx.strokeRect(err.x, err.y, err.w, err.h);
      if (hitCircleRect(player.x, player.y, player.r, err) && now > game.hurtUntil) {
        game.health -= 1;
        game.hurtUntil = now + 900;
        if (game.health <= 0) {
          game.state = "lost";
          setState("lost");
        }
      }
    });

    game.fragments.forEach((fragment) => {
      if (!fragment.taken && Math.hypot(fragment.x - player.x, fragment.y - player.y) < player.r + 8) fragment.taken = true;
      if (!fragment.taken) {
        ctx.shadowColor = "#43ff75";
        ctx.shadowBlur = 12;
        ctx.fillStyle = "#43ff75";
        ctx.fillRect(fragment.x - 4, fragment.y - 4, 8, 8);
        ctx.shadowBlur = 0;
      }
    });

    const collected = game.fragments.filter((fragment) => fragment.taken).length;
    if (collected === 3) {
      ctx.strokeStyle = "#43ff75";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(game.exit.x, game.exit.y, game.exit.r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = "rgba(67,255,117,0.25)";
      ctx.fill();
      if (Math.hypot(game.exit.x - player.x, game.exit.y - player.y) < game.exit.r + player.r) {
        game.state = "won";
        setState("won");
      }
    }

    ctx.shadowColor = "#c83c36";
    ctx.shadowBlur = now < game.hurtUntil ? 18 : 8;
    ctx.fillStyle = "#c83c36";
    ctx.beginPath();
    ctx.arc(player.x, player.y, player.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.fillStyle = "rgba(220,255,220,0.85)";
    ctx.font = `${Math.max(8, Math.floor(w / 34))}px ui-monospace, SFMono-Regular, Consolas, monospace`;
    ctx.fillText(`HP ${game.health}  FRAG ${collected}/3  ${remaining.toFixed(0)}S`, 8, 13);

    if (remaining <= 0 && game.state === "playing") {
      game.state = "lost";
      setState("lost");
    }
  }, []);

  const drawEnd = useCallback((ctx: CanvasRenderingContext2D, now: number) => {
    drawIdle(ctx, now);
    const game = gameRef.current;
    ctx.fillStyle = "rgba(2,7,4,0.72)";
    ctx.fillRect(0, 0, game.w, game.h);
    ctx.fillStyle = game.state === "won" ? "#43ff75" : "#c83c36";
    ctx.font = `${Math.max(12, Math.floor(game.w / 18))}px ui-monospace, SFMono-Regular, Consolas, monospace`;
    ctx.fillText(game.state === "won" ? "BUILD SUCCESSFUL" : "BUILD FAILED", 12, game.h / 2);
  }, [drawIdle]);

  const render = useCallback((now: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !visibleRef.current || !documentVisibleRef.current) return;
    const game = gameRef.current;
    const dt = Math.min(0.035, Math.max(0.001, (now - game.last) / 1000));
    game.last = now;
    if (game.state === "playing" && !reduceMotionRef.current) drawPlaying(ctx, now, dt);
    else if (game.state === "won" || game.state === "lost") drawEnd(ctx, now);
    else drawIdle(ctx, now);
    frameRef.current = requestAnimationFrame(render);
  }, [drawEnd, drawIdle, drawPlaying]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => {
      reduceMotionRef.current = media.matches;
      if (media.matches && gameRef.current.state === "playing") {
        gameRef.current.state = "idle";
        setState("idle");
      }
    };
    updateMotion();
    media.addEventListener("change", updateMotion);
    return () => media.removeEventListener("change", updateMotion);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrapper = wrapRef.current;
    if (!canvas || !wrapper) return;
    const resize = () => {
      const rect = wrapper.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      playableRef.current = rect.width >= 170 && rect.height >= 90;
      setPlayable(playableRef.current);
      gameRef.current.w = Math.max(1, rect.width);
      gameRef.current.h = Math.max(1, rect.height);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (gameRef.current.state === "playing") resetGame();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(wrapper);
    resize();
    return () => ro.disconnect();
  }, [resetGame]);

  useEffect(() => {
    const wrapper = wrapRef.current;
    if (!wrapper) return;
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
      cancelAnimationFrame(frameRef.current ?? 0);
      if (entry.isIntersecting && documentVisibleRef.current) frameRef.current = requestAnimationFrame(render);
    }, { threshold: 0.05 });
    observer.observe(wrapper);
    const onVisibility = () => {
      documentVisibleRef.current = !document.hidden;
      cancelAnimationFrame(frameRef.current ?? 0);
      if (!document.hidden && visibleRef.current) frameRef.current = requestAnimationFrame(render);
    };
    document.addEventListener("visibilitychange", onVisibility);
    frameRef.current = requestAnimationFrame(render);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      cancelAnimationFrame(frameRef.current ?? 0);
    };
  }, [render]);

  useEffect(() => {
    if (state !== "playing") return;
    const down = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        gameRef.current.state = "idle";
        keysRef.current.clear();
        pointerRef.current.active = false;
        setState("idle");
        return;
      }
      if (keys.has(event.key)) {
        event.preventDefault();
        keysRef.current.add(event.key);
      }
    };
    const up = (event: KeyboardEvent) => keysRef.current.delete(event.key);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      keysRef.current.clear();
    };
  }, [state]);

  const setPointer = (event: PointerEvent<HTMLDivElement>, active = true) => {
    const rect = event.currentTarget.getBoundingClientRect();
    pointerRef.current = { active, x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  return (
    <div
      ref={wrapRef}
      className="terminal-mini-game"
      role="button"
      tabIndex={0}
      aria-label="System Breach mini game. Press Enter to debug, Escape to exit."
      aria-disabled={!playable || reduceMotionRef.current}
      onClick={() => state === "idle" && activate()}
      onFocus={() => { focusRef.current = true; }}
      onBlur={() => { focusRef.current = false; }}
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; pointerRef.current.active = false; }}
      onKeyDown={(event) => {
        if (state === "idle" && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          activate();
        }
      }}
      onPointerDown={(event) => {
        if (state === "playing") {
          event.currentTarget.setPointerCapture(event.pointerId);
          setPointer(event);
        }
      }}
      onPointerMove={(event) => state === "playing" && setPointer(event)}
      onPointerUp={(event) => {
        event.currentTarget.releasePointerCapture(event.pointerId);
        pointerRef.current.active = false;
      }}
    >
      <canvas ref={canvasRef} aria-hidden="true" />
      {state === "won" && (
        <button className="terminal-play-again" type="button" onClick={(event) => { event.stopPropagation(); activate(); }}>
          Play Again
        </button>
      )}
    </div>
  );
}
